import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import MagazziniService from 'services/MagazziniService';
import { MAGAZZINI } from '../../configs/AppConfig';
import { CURRENT_COMPANY_MAGAZZINI } from 'constants/AuthConstant';
import DbUtils from 'utils/indexDB';

export const initialState = {
    magazzini: [],
    loadingMagazzini: false,
    loadedMagazzini: false,
    errorMagazzini: false,
    addingMagazzino: false,
    addedMagazzino: false,
    errorAddMagazzino: false,
    currentCompany: "",
    updatingMagazzino: false,
    updatedMagazzino: false,
    errorUpdateMagazzino: false,
    deletingMagazzino: false,
    deletedMagazzino: false,
    errorDeleteMagazzino: false
};

const DB_NAME = 'MyMagazziniDB';
const STORE_NAME = 'warehouses';
const VERSION = 1;
const LAST_SYNC_TIMESTAMP = 'MAGAZZINI_LAST_FULL_SYNC';

function ordinaPerCodicePadre(data) {
    // Crea un dizionario per accesso rapido ai magazzini per codice
    const codMap = {};
    data.forEach(item => {
        codMap[item.cod] = item;
    });

    // Crea un dizionario che mappa codice padre => array di figli
    const figliMap = {};
    data.forEach(item => {
        if (item.codPadre && item.codPadre !== '') {
            if (!figliMap[item.codPadre]) {
                figliMap[item.codPadre] = [];
            }
            figliMap[item.codPadre].push(item);
        }
    });

    // Array per i risultati ordinati
    const risultatiOrdinati = [];
    // Insieme per tenere traccia degli elementi già processati
    const processati = new Set();

    // Ordina i figli in ogni gruppo (opzionale, ma migliora la leggibilità)
    for (const codPadre in figliMap) {
        figliMap[codPadre].sort((a, b) => a.nome.localeCompare(b.nome));
    }

    // Funzione per aggiungere un magazzino e tutti i suoi figli in sequenza
    const aggiungiConFigli = (magazzino) => {
        // Se l'elemento è già stato processato, salta
        if (processati.has(magazzino.cod)) return;

        // Aggiungi il magazzino corrente
        risultatiOrdinati.push(magazzino);
        processati.add(magazzino.cod);

        // Aggiungi tutti i figli diretti di questo magazzino
        const figli = figliMap[magazzino.cod] || [];
        figli.forEach(figlio => {
            // Aggiungi il figlio (se non ancora processato)
            if (!processati.has(figlio.cod)) {
                risultatiOrdinati.push(figlio);
                processati.add(figlio.cod);

                // Processa ricorsivamente i figli dei figli
                aggiungiConFigli(figlio);
            }
        });
    };

    // Prima elabora tutti i magazzini che non hanno un codice padre o sono padri di sé stessi
    const magazziniPrincipali = data.filter(item => !item.codPadre || item.codPadre === '' || item.codPadre === item.cod);

    // Ordina i magazzini principali
    magazziniPrincipali.sort((a, b) => a.nome.localeCompare(b.nome));

    // Elabora ogni magazzino principale e i suoi discendenti
    magazziniPrincipali.forEach(item => {
        aggiungiConFigli(item);
    });

    // Poi elabora eventuali magazzini che non sono stati ancora processati (per sicurezza)
    data.forEach(item => {
        if (!processati.has(item.cod)) {
            aggiungiConFigli(item);
        }
    });

    return risultatiOrdinati;
}

export const loadMagazzini = createAsyncThunk('reset-magazzini', async (data, { rejectWithValue }) => {
    console.log("[loadMagazzini]");

    try {
        // Recupera l'ID dell'azienda corrente
        const currentCompanyId = data.documentId;

        // Recupera l'ultima azienda memorizzata e i magazzini in cache
        const lastCompanyId = localStorage.getItem(CURRENT_COMPANY_MAGAZZINI);
        const cachedWarehouses = await DbUtils.getAll(DB_NAME, VERSION, STORE_NAME);

        // Get the last full sync timestamp
        const lastFullSync = localStorage.getItem(LAST_SYNC_TIMESTAMP);
        const now = new Date().getTime();
        const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours
        const shouldForceFullSync = !lastFullSync || (now - parseInt(lastFullSync)) > oneDayInMs;

        // Force a full sync if:
        // 1. Company has changed
        // 2. There are no cached warehouses
        // 3. It's been more than a day since the last full sync
        if (currentCompanyId !== lastCompanyId || !cachedWarehouses || cachedWarehouses.length === 0 || shouldForceFullSync) {
            console.log('Nuova azienda o nessuna cache trovata o sync giornaliero, fetch completo dal server...');

            const response = await MagazziniService.getSources(currentCompanyId);

            const warehouses = ordinaPerCodicePadre(response.data).map(item => ({
                id: item.id,
                documentId: item.documentId,
                updatedAt: item.updatedAt,
                ...item,
            }));

            // Update last full sync timestamp
            localStorage.setItem(LAST_SYNC_TIMESTAMP, now.toString());

            // Salva i magazzini e l'ID dell'azienda corrente
            await DbUtils.clearAllAndSaveItem(warehouses, DB_NAME, VERSION, STORE_NAME);

            return { magazzini: warehouses, company: currentCompanyId };
        } else {

            console.log('Stessa azienda, verifico aggiornamenti dalla cache...');

            // Logica per l'aggiornamento incrementale

            // Get the server total count to detect if items were deleted
            const countResponse = await MagazziniService.getSourcesCount(currentCompanyId);
            const serverWarehousesCount = countResponse.meta.pagination.total;

            // If server has fewer items than cache, do a full sync to get updated data
            if (serverWarehousesCount < cachedWarehouses.length) {
                console.log('Rilevate cancellazioni nel database remoto, eseguo sync completo...');

                const response = await MagazziniService.getSources(currentCompanyId);
                const warehouses = ordinaPerCodicePadre(response.data).map(item => ({
                    id: item.id,
                    documentId: item.documentId,
                    updatedAt: item.updatedAt,
                    ...item,
                }));

                // Update last full sync timestamp
                localStorage.setItem(LAST_SYNC_TIMESTAMP, now.toString());

                // Replace the cache completely
                await DbUtils.clearAllAndSaveItem(warehouses, DB_NAME, VERSION, STORE_NAME);
                console.log("magazzini aggiornati dopo rilevamento cancellazioni: ", warehouses);

                return { magazzini: warehouses, company: currentCompanyId };
            }

            // Logica esistente per l'aggiornamento incrementale
            const latestUpdatedAt = cachedWarehouses.reduce((latest, warehouse) => {
                const updatedDate = new Date(warehouse.updatedAt);
                return updatedDate > latest ? updatedDate : latest;
            }, new Date(0));

            console.log("Ultimo aggiornamento MAGAZZINI cache:", latestUpdatedAt.toISOString());

            const response = await MagazziniService.getSourcesAfter(currentCompanyId, latestUpdatedAt.toISOString());

            const updatedWarehouses = ordinaPerCodicePadre(response.data).map(item => ({
                id: item.id,
                documentId: item.documentId,
                updatedAt: item.updatedAt,
                ...item,
            }));

            if (updatedWarehouses.length > 0) {
                console.log('ale-debug Ci sono aggiornamenti dal server, aggiorno cache...');
                const mergedWarehouses = DbUtils.mergeItems(cachedWarehouses, updatedWarehouses);
                await DbUtils.saveItem(mergedWarehouses, DB_NAME, VERSION, STORE_NAME);
                console.log("ale-debug mergedWarehouses: ", mergedWarehouses);
                return { magazzini: mergedWarehouses, company: currentCompanyId };
            } else {
                console.log('ale-debug Nessun aggiornamento, uso la cache.');
                const cachedWarehousesOrdered = ordinaPerCodicePadre(cachedWarehouses).map(item => ({
                    id: item.id,
                    documentId: item.documentId,
                    updatedAt: item.updatedAt,
                    ...item,
                }));
                return { magazzini: cachedWarehousesOrdered, company: currentCompanyId };
            }
        }
    } catch (err) {
        console.error(err);
        return rejectWithValue('ERRORE');
    }
});

export const resetMagazzini = createAsyncThunk('carica-magazzini', async (data, { rejectWithValue }) => {
    console.log("[loadMagazzini]");

    try {
        // Recupera l'ID dell'azienda corrente
        const currentCompanyId = data.documentId;

        console.log('Nuova azienda o nessuna cache trovata, fetch completo dal server...');
        const response = await MagazziniService.getSources(currentCompanyId);

        const warehouses = ordinaPerCodicePadre(response.data).map(item => ({
            id: item.id,
            documentId: item.documentId,
            updatedAt: item.updatedAt,
            ...item,
        }));

        // Salva i magazzini e l'ID dell'azienda corrente
        await DbUtils.clearAllAndSaveItem(warehouses, DB_NAME, VERSION, STORE_NAME);

        return { magazzini: warehouses, company: currentCompanyId };

    } catch (err) {
        console.error(err);
        return rejectWithValue('ERRORE');
    }
});

export const nuovoMagazzino = createAsyncThunk('aggiungi-magazzino', async (body, { rejectWithValue, getState }) => {
    try {
        const response = await MagazziniService.addSource(body);
        return response.data;
    } catch (err) {
        console.error("err", err);
        return rejectWithValue('ERRORE');
    }
});

export const updateMagazzino = createAsyncThunk('aggiorna-magazzino', async ({ documentId, data }, { rejectWithValue }) => {
    try {
        const response = await MagazziniService.updateStorage(documentId, data);
        return response.data;
    } catch (err) {
        console.error("err", err);
        return rejectWithValue('ERRORE');
    }
});

export const deleteMagazzino = createAsyncThunk('elimina-magazzino', async (documentId, { rejectWithValue, getState }) => {
    try {
        const response = await MagazziniService.deleteStorage(documentId);

        // Aggiorna anche l'IndexDB rimuovendo il magazzino eliminato
        try {
            const cachedWarehouses = await DbUtils.getAll(DB_NAME, VERSION, STORE_NAME);
            if (cachedWarehouses && cachedWarehouses.length > 0) {
                // Filtra i magazzini rimuovendo quello con il documentId specificato
                const updatedWarehouses = cachedWarehouses.filter(warehouse => warehouse.documentId !== documentId);
                // Salva i magazzini aggiornati nell'IndexDB
                await DbUtils.clearAllAndSaveItem(updatedWarehouses, DB_NAME, VERSION, STORE_NAME);
                console.log('Magazzino eliminato anche da IndexDB:', documentId);
            }
        } catch (dbError) {
            console.error('Errore durante l\'aggiornamento di IndexDB:', dbError);
        }

        return { documentId, response: response.data };
    } catch (err) {
        console.error("err", err);
        return rejectWithValue('ERRORE');
    }
});

export const magazzini = createSlice({
    name: 'magazzini',
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadMagazzini.pending, (state) => {
                state.loadingMagazzini = true
                state.loadedMagazzini = false
                state.errorMagazzini = false
            })
            .addCase(loadMagazzini.fulfilled, (state, action) => {
                state.loadingMagazzini = false
                state.magazzini = action.payload.magazzini
                state.errorMagazzini = false
                state.loadedMagazzini = true
                state.currentCompany = action.payload.company
                localStorage.setItem(CURRENT_COMPANY_MAGAZZINI, action.payload.company)
            })
            .addCase(loadMagazzini.rejected, (state, action) => {
                state.loadingMagazzini = false
                state.magazzini = []
                state.loadedMagazzini = true
                state.errorMagazzini = true
            })
             .addCase(resetMagazzini.pending, (state) => {
                state.loadingMagazzini = true
                state.loadedMagazzini = false
                state.errorMagazzini = false
            })
            .addCase(resetMagazzini.fulfilled, (state, action) => {
                state.loadingMagazzini = false
                state.magazzini = action.payload.magazzini
                state.errorMagazzini = false
                state.loadedMagazzini = true
                state.currentCompany = action.payload.company
                localStorage.setItem(CURRENT_COMPANY_MAGAZZINI, action.payload.company)
            })
            .addCase(resetMagazzini.rejected, (state, action) => {
                state.loadingMagazzini = false
                state.magazzini = []
                state.loadedMagazzini = true
                state.errorMagazzini = true
            })
            .addCase(nuovoMagazzino.pending, (state) => {
                state.addingMagazzino = true
                state.addedMagazzino = false
                state.errorAddMagazzino = false
            })
            .addCase(nuovoMagazzino.fulfilled, (state, action) => {
                state.addingMagazzino = false
                state.addedMagazzino = true
                state.errorAddMagazzino = false
            })
            .addCase(nuovoMagazzino.rejected, (state) => {
                state.addingMagazzino = false
                state.addedMagazzino = false
                state.errorAddMagazzino = true
            })
            .addCase(updateMagazzino.pending, (state) => {
                state.updatingMagazzino = true
                state.updatedMagazzino = false
                state.errorUpdateMagazzino = false
            })
            .addCase(updateMagazzino.fulfilled, (state, action) => {
                state.updatingMagazzino = false
                state.updatedMagazzino = true
                state.errorUpdateMagazzino = false
            })
            .addCase(updateMagazzino.rejected, (state) => {
                state.updatingMagazzino = false
                state.updatedMagazzino = false
                state.errorUpdateMagazzino = true
            })
            .addCase(deleteMagazzino.pending, (state) => {
                state.deletingMagazzino = true
                state.deletedMagazzino = false
                state.errorDeleteMagazzino = false
            })
            .addCase(deleteMagazzino.fulfilled, (state, action) => {
                state.deletingMagazzino = false
                state.deletedMagazzino = true
                state.errorDeleteMagazzino = false
                // Rimuove il magazzino cancellato dall'array dei magazzini
                state.magazzini = state.magazzini.filter(
                    magazzino => magazzino.documentId !== action.payload.documentId
                )
            })
            .addCase(deleteMagazzino.rejected, (state) => {
                state.deletingMagazzino = false
                state.deletedMagazzino = false
                state.errorDeleteMagazzino = true
            })
    }
})

export default magazzini.reducer;